import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi, useRawApi } from '@/hooks/use-api'
import { exportErrorMessage } from '@/features/leads/export-helpers'

export type TeamMe = {
  org: { id: string; name: string } | null
  role: string | null
  is_manager: boolean
}

/**
 * The signed-in user's client team membership (GET /api/team/me). Drives
 * the manager-only "Team" nav entry and the /team console gate. Never
 * errors for users in no team: the endpoint answers is_manager=false.
 */
export function useTeamMe() {
  const api = useApi()
  return useQuery({
    queryKey: ['team', 'me'],
    queryFn: async (): Promise<TeamMe> => {
      const res = await api<TeamMe>('/api/team/me')
      if (!res.success || !res.data) return { org: null, role: null, is_manager: false }
      return res.data
    },
    staleTime: 5 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Team-only panels (leads worker routes/team.ts). Query keys live under
// ['team', ...] so they never mix with the Staff Console caches.
// ---------------------------------------------------------------------------

const POLL_MS = 30_000

export type FunnelCounts = { invites_sent: number; accepted: number; replies: number; calls_booked: number }

/** GET /api/team/overview -> rep_funnel (rolling 7-day weeks). */
export type TeamRepFunnel = {
  window: 'rolling_7d'
  reps: Array<{ user_id: string; email: string; full_name: string | null; this_week: FunnelCounts; last_week: FunnelCounts }>
  total: { this_week: FunnelCounts; last_week: FunnelCounts }
}

export type TeamApproval = {
  id: string
  user_id: string
  lead_id: string
  message_type: string
  content: string
  status: 'pending_approval' | 'pending_send'
  hold_reason: 'review' | 'stuck'
  qg_score: number | null
  qg_feedback: string | null
  created_at: string
  handed_to_rep_at: string | null
  lead_name: string | null
  lead_linkedin_url: string | null
  lead_company: string | null
  owner_email: string
  owner_name: string | null
  campaign_name: string | null
}

export type TeamBooking = {
  id: string
  user_id: string
  lead_id: string
  provider: string
  status: string
  chosen_slot: string | null
  invitee_email: string | null
  timezone: string | null
  meet_url: string | null
  created_at: string
  lead_name: string | null
  lead_linkedin_url: string | null
  lead_company: string | null
  owner_email: string
  owner_name: string | null
}

export type TeamRepHealth = {
  user_id: string
  email: string
  full_name: string | null
  linkedin: { status: string; name: string | null; tier: string | null; tier_checked_at: string | null } | null
  sends_today: number
  daily_cap: number
  last_send_at: string | null
  rate_limited_since: string | null
  invites_paused_since: string | null
}

export type TeamClaim = {
  profile_key: string
  user_id: string
  lead_id: string
  claimed_at: string
  lead_name: string | null
  linkedin_url: string | null
  company: string | null
  lead_status: string | null
  owner_email: string | null
  owner_name: string | null
}

export type TeamDoNotContact = {
  profile_key: string
  linkedin_url: string | null
  reason: string
  created_at: string
  source_email: string | null
  source_name: string | null
}

function unwrap<T>(res: { success: boolean; data?: T; error?: string }): T {
  if (!res.success || res.data === undefined) throw new Error(res.error ?? 'Failed')
  return res.data
}

const withUser = (path: string, userId: string | null) => (userId ? `${path}?user_id=${encodeURIComponent(userId)}` : path)

export function useTeamApprovals(filterUserId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['team', 'approvals', filterUserId],
    queryFn: async () => unwrap(await api<{ approvals: TeamApproval[] }>(withUser('/api/team/approvals', filterUserId))).approvals,
    refetchInterval: POLL_MS,
  })
}

/** Approve (send through the rep's own send path) or hand the draft to the rep. */
export function useTeamApprovalAction() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; action: 'approve' | 'handoff' }) =>
      unwrap(await api<{ id: string; status: string }>(`/api/team/approvals/${encodeURIComponent(vars.id)}/${vars.action}`, { method: 'POST' })),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['team', 'approvals'] })
    },
  })
}

export function useTeamBookings(filterUserId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['team', 'bookings', filterUserId],
    queryFn: async () => unwrap(await api<{ bookings: TeamBooking[] }>(withUser('/api/team/bookings', filterUserId))).bookings,
    refetchInterval: POLL_MS,
  })
}

export function useTeamHealth() {
  const api = useApi()
  return useQuery({
    queryKey: ['team', 'health'],
    queryFn: async () => unwrap(await api<{ reps: TeamRepHealth[] }>('/api/team/health')).reps,
    refetchInterval: POLL_MS,
  })
}

export function useTeamOwnership(search: string) {
  const api = useApi()
  const q = search.trim()
  return useQuery({
    queryKey: ['team', 'ownership', q],
    queryFn: async () =>
      unwrap(await api<{ claims: TeamClaim[]; total: number }>(q ? `/api/team/ownership?q=${encodeURIComponent(q)}` : '/api/team/ownership')),
  })
}

export function useTeamDoNotContact() {
  const api = useApi()
  return useQuery({
    queryKey: ['team', 'do-not-contact'],
    queryFn: async () => unwrap(await api<{ entries: TeamDoNotContact[] }>('/api/team/do-not-contact')).entries,
  })
}

/**
 * Downloads the compliance archive (GET /api/team/export/messages) as CSV.
 * The endpoint returns text/csv, not the JSON envelope, so this reads the
 * raw Response, like the leads export.
 */
export function useExportTeamMessages() {
  const rawFetch = useRawApi()
  return useMutation({
    mutationFn: async (range: { from: string; to: string }) => {
      const res = await rawFetch(`/api/team/export/messages?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`)
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(exportErrorMessage(body, res.status))
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `team-messages-${range.from}-to-${range.to}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      // Revoking in the same tick can cancel the download in Safari / Firefox.
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
    },
  })
}
